import { useEffect, useRef } from 'react'

// Global counter for tracking modal instances
let modalInstanceCounter = 0

/**
 * Hook for handling mobile back button
 * When the back button is pressed while a modal is open, it closes the modal,
 * When no modal is open, it performs the browser's default back navigation
 */
export const useMobileBackButton = (
  isModalOpen: boolean,
  onModalClose: () => void,
  onBrowserBack?: () => void,
) => {
  const isModalOpenRef = useRef(isModalOpen)
  const onModalCloseRef = useRef(onModalClose)
  const onBrowserBackRef = useRef(onBrowserBack)
  const closedByBackButtonRef = useRef(false)
  const modalIdRef = useRef<number | null>(null)

  // Update refs
  useEffect(() => {
    isModalOpenRef.current = isModalOpen
    onModalCloseRef.current = onModalClose
    onBrowserBackRef.current = onBrowserBack
  }, [isModalOpen, onModalClose, onBrowserBack])

  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      // Only handle when modal is open
      if (isModalOpenRef.current) {
        // event.state represents the "state to return to"
        // Not the current modal state, but returning to the previous state
        const currentStateModalId = event.state?.modalId
        const thisModalId = modalIdRef.current

        // If the state to return to is before this modal (i.e., this modal should be closed)
        // If currentStateModalId is less than thisModalId or null, this modal should be closed
        const shouldCloseThisModal =
          currentStateModalId === null ||
          currentStateModalId === undefined ||
          (typeof currentStateModalId === 'number' &&
            typeof thisModalId === 'number' &&
            currentStateModalId < thisModalId)

        if (shouldCloseThisModal) {
          event.preventDefault()
          closedByBackButtonRef.current = true
          onModalCloseRef.current()
          // Execute custom action after closing modal if available
          if (onBrowserBackRef.current) {
            onBrowserBackRef.current()
          }
          return
        }
      }

      // If modal is closed, perform browser default action or custom action
      if (!isModalOpenRef.current && onBrowserBackRef.current) {
        onBrowserBackRef.current()
      }
    }

    const handleBeforeUnload = () => {
      // Close modal first when trying to leave the page while modal is open
      if (isModalOpenRef.current) {
        onModalCloseRef.current()
      }
    }

    // Add popstate event listener only when modal is open
    if (isModalOpen) {
      // Assign new modal ID
      if (modalIdRef.current === null) {
        modalIdRef.current = ++modalInstanceCounter
      }

      // Add state to history (with modal ID)
      window.history.pushState({ modalOpen: true, modalId: modalIdRef.current }, '')

      window.addEventListener('popstate', handlePopState)
      window.addEventListener('beforeunload', handleBeforeUnload)
    }

    return () => {
      window.removeEventListener('popstate', handlePopState)
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [isModalOpen])
  // Clean up state when the modal closes
  useEffect(() => {
    if (!isModalOpen) {
      // Reset flag if the modal was closed via the back button
      if (closedByBackButtonRef.current) {
        closedByBackButtonRef.current = false
      }
      modalIdRef.current = null
    }
  }, [isModalOpen])
}

/**
 * Manage modal state with body class on mobile
 */
export const useModalBodyClass = (isModalOpen: boolean) => {
  useEffect(() => {
    if (isModalOpen) {
      document.body.classList.add('modal-open')
    } else {
      document.body.classList.remove('modal-open')
    }

    return () => {
      document.body.classList.remove('modal-open')
    }
  }, [isModalOpen])
}
